/**
 * NotificationSoundManager - Manages notification sounds only
 * 
 * Singleton utility that:
 * - Plays a notification sound when a chat needs user attention
 * - Speaks in TWO voices (human_message_notification_sound_prd.md): 'agent' = the classic
 *   bell (agent chats, employee shifts, HC approvals — the default, all legacy call sites
 *   unchanged); 'human' = the warmer chime for human-authored DM/channel pings
 * - Respects user mute preference (ONE switch for both variants; synced from server, cached
 *   in localStorage)
 * - Elects a single "leader" tab to play sounds (prevents echo across multiple tabs)
 * - Emits 'notification-sound-played' on eventBus when sound actually plays (payload carries
 *   the variant — additive; subscribers that ignore it keep working)
 * 
 * Badge state is managed server-side (attention_at + user_last_seen_at on PostOffice).
 * This manager is ONLY responsible for sound playback.
 */

import eventBus from './eventBus';

const MUTE_STORAGE_KEY = 'cellcog_notification_sound_muted';
const LEADER_CHANNEL_NAME = 'cellcog-notification-leader';
const DEBOUNCE_MS = 500;
// Window used by hasPlayedRecently() — long enough to cover the gap between the
// HC "commands pending" sound and the same chat's operating→false status update
// (stop_operation can take many seconds: git ops, log push, uploads, container stop).
const RECENT_PLAY_WINDOW_MS = 5 * 60 * 1000;

// Sound variants (human_message_notification_sound_prd.md): the ring's VOICE follows the
// semantic author of the attention event. Unknown/missing variant falls back to 'agent'
// (today's bell) so old backends and legacy call sites are always safe.
const SOUND_SRC = {
  agent: '/sounds/cellcog-notify.mp3',
  human: '/sounds/cellcog-notify-human.mp3',
};

class NotificationSoundManagerClass {
  constructor() {
    this.audios = {}; // variant -> Audio
    this.muted = false;
    this.initialized = false;
    this._lastPlayTime = 0;
    this._lastPlayByChat = new Map(); // chatId -> last ACTUAL play timestamp (leader tab only)

    // Leader election state
    this._tabId = Math.random().toString(36).substring(2, 10);
    this._isLeader = false;
    this._channel = null;
  }

  /**
   * Initialize the manager. Call once on app startup.
   * Preloads the audio files, reads mute preference, and starts leader election.
   */
  init() {
    if (this.initialized) return;

    Object.entries(SOUND_SRC).forEach(([variant, src]) => {
      try {
        const audio = new Audio(src);
        audio.preload = 'auto';
        audio.volume = 1.0;
        audio.load();
        this.audios[variant] = audio;
      } catch (err) {
        console.warn(`[NotificationSound] Failed to initialize audio (${variant}):`, err);
      }
    });

    // Read mute preference from localStorage (fast cache before server sync)
    try {
      const stored = localStorage.getItem(MUTE_STORAGE_KEY);
      this.muted = stored === 'true';
    } catch {
      this.muted = false;
    }

    // Setup cross-tab leader election via BroadcastChannel
    this._initLeaderElection();

    this.initialized = true;
  }

  /**
   * Play notification sound for a chat.
   * Sound plays if this tab is the leader, user hasn't muted, and debounce allows.
   * Emits 'notification-sound-played' on eventBus when sound actually plays.
   * 
   * @param {string} chatId - The chat that needs attention
   * @param {string} [variant='agent'] - 'agent' (classic bell) | 'human' (human-message
   *   chime). Unknown values fall back to 'agent'.
   */
  play(chatId, variant = 'agent') {
    this.init(); // Self-healing: auto-initialize if singleton was reset (e.g., HMR)
    if (!this.initialized || !chatId) return;
    if (this.muted) return;

    // Only the leader tab plays the actual sound
    if (!this._isLeader) return;

    // Debounce — prevent double sounds from overlapping triggers. Shared across variants
    // (one CHANNEL_CHANGED fan-out hitting several channels must not machine-gun sounds);
    // per-message re-rings arrive well outside this 500ms floor.
    const now = Date.now();
    if (now - this._lastPlayTime > DEBOUNCE_MS) {
      this._lastPlayTime = now;
      this._lastPlayByChat.set(chatId, now);
      const resolvedVariant = SOUND_SRC[variant] ? variant : 'agent';
      try {
        const audio = this.audios[resolvedVariant] || this.audios.agent;
        if (audio) {
          audio.currentTime = 0;
          audio.play().catch(() => {});
        }
      } catch {
        // Audio playback failed silently
      }

      // Notify subscribers (e.g., sidebar speaker animation)
      eventBus.emit('notification-sound-played', { chatId, variant: resolvedVariant, timestamp: now });
    }
  }

  /**
   * Set mute state and persist to localStorage.
   * @param {boolean} muted
   */
  setMuted(muted) {
    this.muted = !!muted;
    try {
      localStorage.setItem(MUTE_STORAGE_KEY, String(this.muted));
    } catch {
      // localStorage unavailable
    }
    // Notify subscribers of mute state change (sidebar bell, profile settings)
    eventBus.emit('notification-mute-changed', { muted: this.muted });
  }

  /**
   * @returns {boolean} Current mute state
   */
  isMuted() {
    return this.muted;
  }

  /**
   * Whether a notification sound ACTUALLY played for this chat recently.
   * Used to dedupe overlapping triggers that represent the same logical
   * "chat needs attention" event — e.g. the HUMAN_COMPUTER_COMMANDS_PENDING
   * sound (HumanComputerContext) followed seconds later by the same HC pause's
   * operating→false CHAT_STATUS_UPDATE (ChatListContext).
   *
   * Only the leader tab records plays, which is exactly right: only the leader
   * tab produces sound, so only it needs to dedupe. Non-leader tabs return
   * false here and their play() calls are no-ops anyway.
   *
   * @param {string} chatId
   * @param {number} [windowMs]
   * @returns {boolean}
   */
  hasPlayedRecently(chatId, windowMs = RECENT_PLAY_WINDOW_MS) {
    const last = this._lastPlayByChat.get(chatId);
    return !!last && (Date.now() - last) < windowMs;
  }

  // --- Leader Election ---

  _initLeaderElection() {
    try {
      this._channel = new BroadcastChannel(LEADER_CHANNEL_NAME);
    } catch {
      // BroadcastChannel not supported — this tab is always leader (fallback)
      this._isLeader = true;
      return;
    }

    this._channel.onmessage = (event) => {
      const { type, tabId } = event.data;
      if (type === 'claim-leader') {
        this._isLeader = (tabId === this._tabId);
      } else if (type === 'tab-closing') {
        this._claimLeadership();
      }
    };

    // Claim leadership on init (first tab to load becomes leader)
    this._claimLeadership();

    // Claim leadership when this tab gains focus
    this._visibilityHandler = () => {
      if (!document.hidden) {
        this._claimLeadership();
      }
    };
    document.addEventListener('visibilitychange', this._visibilityHandler);

    // Announce departure when tab closes
    this._beforeUnloadHandler = () => {
      if (this._isLeader && this._channel) {
        this._channel.postMessage({ type: 'tab-closing', tabId: this._tabId });
      }
    };
    window.addEventListener('beforeunload', this._beforeUnloadHandler);
  }

  _claimLeadership() {
    this._isLeader = true;
    if (this._channel) {
      try {
        this._channel.postMessage({ type: 'claim-leader', tabId: this._tabId });
      } catch {
        // Channel closed or unavailable
      }
    }
  }
}

const NotificationSoundManager = new NotificationSoundManagerClass();
export default NotificationSoundManager;
